import { useCallback } from 'react';

import type { UUID } from '@aline/common';
import { gql } from '@apollo/client';
import { Apollo } from 'src/gql/generated';
import { labelTypename } from 'src/helpers';
import type { LabelSubset } from 'src/types';

export function useGetWorkflowLabels(workflowId?: UUID | null) {
  const { data, loading } = Apollo.useGetWorkflowLabelsQuery({
    skip: !workflowId,
    variables: { workflowId: workflowId ?? '' },
  });

  return {
    loading,
    workflow: data?.workflow ?? null,
    workflowLabels: data?.workflow?.labels ?? [],
  };
}

export function useGetUserLabels(options?: Parameters<typeof Apollo.useGetUserLabelsQuery>[0]) {
  const { data, loading, refetch } = Apollo.useGetUserLabelsQuery(options);

  return {
    labels: data?.labels ?? [],
    loading,
    refetch,
  };
}

export function useFindOrCreateLabel() {
  const userLabelsKey: keyof Apollo.Query = 'labels';
  const [findOrCreateLabel, { loading }] = Apollo.useFindOrCreateLabelMutation();
  const safeFindOrCreateLabel = useCallback(
    async (labelText: string) => {
      const { data } = await findOrCreateLabel({
        update: (cache, { data }) => {
          if (!data) return;

          const newRef = cache.writeFragment({
            data: data.findOrCreateLabel,
            fragment: gql`
              fragment NewLabel on Label {
                __typename
                id
              }
            `,
          });
          if (!newRef) return;

          cache.modify({
            fields: {
              [userLabelsKey]: (refs = []) => {
                return [...refs, newRef];
              },
            },
          });
        },
        variables: { labelText },
      });

      return data?.findOrCreateLabel ?? null;
    },
    [findOrCreateLabel]
  );

  return {
    findOrCreateLabel: safeFindOrCreateLabel,
    loading,
  };
}

export function useSetDocumentLabels() {
  const [setDocumentLabels, { loading }] = Apollo.useSetDocumentLabelsMutation();
  const safeSetDocumentLabels = useCallback(
    async ({ documentId, labels }: { documentId: string; labels: LabelSubset[] }) => {
      return setDocumentLabels({
        optimisticResponse: {
          __typename: 'Mutation',
          setDocumentLabels: {
            __typename: 'Document',
            id: documentId,
            labels,
          },
        },
        variables: { documentId, labelIds: labels.map(l => l.id) },
      });
    },
    [setDocumentLabels]
  );

  return {
    loading,
    setDocumentLabels: safeSetDocumentLabels,
  };
}

export function useSetWorkflowLabels() {
  const [setWorkflowLabels, { loading }] = Apollo.useSetWorkflowLabelsMutation();
  const safeSetWorkflowLabels = useCallback(
    async ({ labels, workflowId }: { labels: LabelSubset[]; workflowId: string }) => {
      return setWorkflowLabels({
        optimisticResponse: {
          __typename: 'Mutation',
          setWorkflowLabels: {
            __typename: 'WorkflowResponse',
            workflow: {
              __typename: 'Workflow',
              id: workflowId,
              labels,
            },
          },
        },
        variables: { labelIds: labels.map(l => l.id), workflowId },
      });
    },
    [setWorkflowLabels]
  );

  return {
    loading,
    setWorkflowLabels: safeSetWorkflowLabels,
  };
}

export function useDeleteLabel() {
  const [deleteLabel, { loading }] = Apollo.useDeleteLabelMutation();
  const safeDeleteLabel = useCallback(
    async (id: string) => {
      return deleteLabel({
        update(cache, { data }) {
          if (data?.deleteLabel) {
            cache.evict({
              id: cache.identify({ __typename: labelTypename, id: data.deleteLabel }),
            });
            cache.gc();
          }
        },
        variables: { id },
      });
    },
    [deleteLabel]
  );

  return {
    deleteLabel: safeDeleteLabel,
    loading,
  };
}
